import type { FilterDefinition, FilterOption } from './filter-types';

export interface FilterMentionToken {
  /** Index of the `@` in the input value. */
  start: number;
  /** Everything between the `@` and the caret. */
  query: string;
}

/**
 * Find an `@filter` token ending at the caret.
 *
 * The `@` has to open a word — start of the value or after whitespace — and
 * nothing between it and the caret may be whitespace or a second `@`. That is
 * the same rule the composer's mention menu uses, so `alice@example.com` and
 * `a@b` never turn the search box into a menu.
 */
export function detectFilterMention(text: string, caret: number): FilterMentionToken | null {
  for (let i = caret - 1; i >= 0; i--) {
    const char = text[i];
    if (char === '@') {
      const prev = i === 0 ? '' : text[i - 1];
      if (i === 0 || /\s/.test(prev)) return { start: i, query: text.slice(i + 1, caret) };
      return null;
    }
    if (/\s/.test(char)) return null;
  }
  return null;
}

export interface FilterMentionScope {
  /** The facet's id, as typed before the colon. */
  facetKey: string;
  /** What was typed after the colon, to match values against. */
  valueQuery: string;
}

/**
 * Split a scoped token: `owner:ali` picks the owner facet and searches its
 * values for "ali". An unscoped query returns null and searches facets instead.
 */
export function parseFilterMentionScope(query: string): FilterMentionScope | null {
  const colon = query.indexOf(':');
  if (colon < 0) return null;
  return { facetKey: query.slice(0, colon), valueQuery: query.slice(colon + 1) };
}

/** Whether the token is a bare scope (`owner:`), i.e. backspacing leaves `@`. */
export function isBareScope(query: string): boolean {
  return /^[^\s:]+:$/.test(query);
}

function matches(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query.trim().toLowerCase());
}

/** Facets whose id or label contains the query. */
export function matchFilterDefinitions(
  definitions: FilterDefinition[],
  query: string,
): FilterDefinition[] {
  if (query.trim() === '') return definitions;
  return definitions.filter(
    (definition) => matches(definition.id, query) || matches(definition.label, query),
  );
}

/** Values whose label contains the query. */
export function matchFilterOptions(options: FilterOption[], query: string): FilterOption[] {
  if (query.trim() === '') return options;
  return options.filter((option) => matches(option.label, query));
}
